import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getAuthHeader } from '../lib/authSession';
import { opensInBrowser } from '../utils/attachmentPreview';

interface Props {
  url: string;
  filename?: string;
  mimeType?: string;
  onClose: () => void;
}

function resolveType(filename?: string, mimeType?: string): 'pdf' | 'image' | 'other' {
  const name = (filename || '').toLowerCase();
  const mt = (mimeType || '').toLowerCase();
  if (mt === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  if (mt.startsWith('image/') || /\.(png|jpe?g|webp|gif|svg)$/.test(name)) return 'image';
  return 'other';
}

// Canonical auth header (lib/authSession) — no more per-file token reads.
const getAuthHeaders = (): HeadersInit => getAuthHeader();

export const AttachmentPreviewModal: React.FC<Props> = ({ url, filename, mimeType, onClose }) => {
  const type = resolveType(filename, mimeType);
  const canOpenInTab = opensInBrowser(filename, mimeType);
  const displayName = filename || 'Attachment';

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const blobRef = useRef<string | null>(null);

  useEffect(() => {
    setBlobUrl(null);
    setLoadState('loading');

    // Fetch with auth header → create blob URL so iframe/img work cross-origin
    fetch(url, { headers: getAuthHeaders() })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        // For PDFs without explicit type, force the MIME so the browser renders inline
        const forcedType =
          type === 'pdf' ? 'application/pdf' :
          type === 'image' ? (blob.type || 'image/png') :
          blob.type;
        const typed = new Blob([blob], { type: forcedType });
        const objectUrl = URL.createObjectURL(typed);
        blobRef.current = objectUrl;
        setBlobUrl(objectUrl);
        setLoadState('ready');
      })
      .catch(() => setLoadState('error'));

    return () => {
      if (blobRef.current) {
        URL.revokeObjectURL(blobRef.current);
        blobRef.current = null;
      }
    };
  }, [url, type]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const content = (
    <div
      className="fixed inset-0 z-[80] flex flex-col bg-black/70 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-slate-200 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-slate-400 text-base">📎</span>
          <span className="text-sm font-semibold text-slate-800 truncate">{displayName}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
          {/* Only for files a tab can show: for xlsx/docx the server sends
              `attachment`, so this link downloaded instead (USC-BUG-0054). */}
          {canOpenInTab && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
              <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
            </svg>
            Open in new tab
          </a>
          )}
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
            aria-label="Close preview"
          >
            <span className="text-xl leading-none">×</span>
          </button>
        </div>
      </div>

      {/* Preview area */}
      <div className="flex-1 min-h-0 overflow-auto flex items-center justify-center p-4">

        {/* Loading */}
        {loadState === 'loading' && (
          <div className="flex flex-col items-center gap-3 text-white/70">
            <svg className="animate-spin h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span className="text-sm">Loading attachment…</span>
          </div>
        )}

        {/* Error */}
        {loadState === 'error' && (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-sm">
            <div className="text-4xl mb-3">⚠️</div>
            <div className="text-sm font-semibold text-slate-800 mb-1">Could not load attachment</div>
            <div className="text-xs text-slate-500 mb-5">The file may have been moved or you may not have access.</div>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600"
            >
              Try opening in new tab
            </a>
          </div>
        )}

        {/* PDF */}
        {loadState === 'ready' && blobUrl && type === 'pdf' && (
          <iframe
            src={blobUrl}
            title={displayName}
            className="w-full h-full rounded shadow-lg bg-white"
            style={{ minHeight: '75vh' }}
          />
        )}

        {/* Image */}
        {loadState === 'ready' && blobUrl && type === 'image' && (
          <img
            src={blobUrl}
            alt={displayName}
            className="max-w-full max-h-full object-contain rounded shadow-lg"
          />
        )}

        {/* Other file types */}
        {loadState === 'ready' && type === 'other' && (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-sm">
            <div className="text-4xl mb-3">📄</div>
            <div className="text-sm font-semibold text-slate-800 mb-1">{displayName}</div>
            <div className="text-xs text-slate-500 mb-5">This file type can't be previewed inline.</div>
            {/* Downloads the BYTES THIS MODAL ALREADY HOLDS, not the API URL again.
                A browser-initiated download cannot send the Authorization header: the app
                keeps its JWT in storage and the server sets no cookie, so /files answered
                401 and Chrome reported "Couldn't download - Needs authorization", while
                "Open in new tab" worked because that URL happened to be signed
                (ClickUp 14yj85ny3y5). The blob was fetched WITH the header seconds ago, is
                same-origin, and cannot expire mid-preview the way a 10-minute signature can. */}
            <a
              href={blobUrl || url}
              download={filename}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              Download
            </a>
            {canOpenInTab && (
              <a href={url} target="_blank" rel="noreferrer" className="block mt-2 text-xs text-primary-500 hover:underline">
                Open in new tab
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};
